import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes, FaRobot, FaRedo } from "react-icons/fa";
import MessageBubble from "../components/MessageBubble";
import { buildApiUrl } from '../utils/api';

const CHATBOT_ENDPOINT = buildApiUrl('/api/chatbot/chat');

// Debug apenas em desenvolvimento
if (process.env.NODE_ENV === 'development') {
  console.log('Chatbot endpoint:', CHATBOT_ENDPOINT);
}
const REQUEST_TIMEOUT = 15000; // 15 segundos
const MAX_RETRIES = 2;

// Mapeia o humor do bot pelo conteúdo da pergunta (para emoji reativo)
const getBotEmoji = (text) => {
  const q = (text || "").toLowerCase();
  if (q.includes("erro") || q.includes("problema")) return "😟";
  if (q.includes("pagamento") || q.includes("preço") || q.includes("pix") || q.includes("cartão")) return "💰";
  if (q.includes("suporte") || q.includes("ajuda") || q.includes("contato")) return "🤓";
  if (q.includes("entrega") || q.includes("prazo") || q.includes("frete")) return "🚚";
  return "🤖";
};

// Delay aleatório para parecer natural (por letra)
const randomCharDelay = () => Math.floor(Math.random() * 30) + 20; // 20–50ms/char

function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "m1", sender: "bot", text: "Olá! Como posso te ajudar hoje? 😊" },
  ]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [botEmoji, setBotEmoji] = useState("🤖");
  const [bubblePulse, setBubblePulse] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const typingAbortRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Carrega o som de notificação a partir de /public/sounds
  useEffect(() => {
    const audio = new Audio("/sounds/notification.mp3");
    audio.load();
    audioRef.current = audio;
  }, []);

  // Scroll automático para a última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  // Animação sutil do balão (pulse) enquanto pensa
  useEffect(() => {
    let t;
    if (isThinking) {
      setBubblePulse(true);
      t = setTimeout(() => setBubblePulse(false), 800);
    } else {
      setBubblePulse(false);
    }
    return () => clearTimeout(t);
  }, [isThinking]);

  const playNotification = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch (_) {}
  }, []);

  // Simula digitação letra por letra
  const typeMessage = useCallback(async (fullText) => {
    typingAbortRef.current = false;

    // Adiciona uma mensagem temporária do bot
    const tempId = `bot-temp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempId, sender: "bot", text: "" }]);

    let current = "";
    for (let i = 0; i < fullText.length; i++) {
      if (typingAbortRef.current) return; // interrompe se desmontar
      current += fullText[i];
      // Capturar o valor atual de current para evitar referência insegura no loop
      const currentText = current;
      setMessages((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((m) => m.id === tempId);
        if (idx !== -1) copy[idx] = { ...copy[idx], text: currentText };
        return copy;
      });
      // Pausas naturais: um pouco mais de delay depois de pontuação e espaços
      const ch = fullText[i];
      let delay = randomCharDelay();
      if (ch === "." || ch === "," || ch === "!" || ch === "?") delay += 150;
      if (ch === " ") delay += 20;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
    }

    // Substitui a temp pela versão final (garante estado consistente)
    setMessages((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex((m) => m.id === tempId);
      if (idx !== -1) copy[idx] = { id: `bot-${Date.now()}`, sender: "bot", text: fullText };
      return copy;
    });

    playNotification();
  }, [playNotification]);

  // Função para fazer requisição com timeout e retry
  const fetchWithTimeout = useCallback(async (question, retries = 0) => {
    // Criar novo AbortController para esta requisição
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Criar timeout
    const timeoutId = setTimeout(() => {
      abortControllerRef.current?.abort();
    }, REQUEST_TIMEOUT);

    try {
      const response = await fetch(CHATBOT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
        signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Se for rate limit, não tentar novamente
        if (response.status === 429) {
          throw new Error(errorData.answer || "Muitas requisições. Por favor, aguarde.");
        }
        
        // Se for erro do servidor e ainda temos tentativas
        if (response.status >= 500 && retries < MAX_RETRIES) {
          throw new Error("RETRY");
        }
        
        throw new Error(errorData.answer || "Erro ao processar sua pergunta.");
      }

      const data = await response.json();
      return data;
    } catch (err) {
      clearTimeout(timeoutId);
      
      if (err.name === 'AbortError') {
        if (retries < MAX_RETRIES) {
          throw new Error("RETRY");
        }
        throw new Error("A requisição demorou muito. Por favor, tente novamente.");
      }
      
      throw err;
    }
  }, []);

  const sendMessage = useCallback(async (questionText = null, isRetry = false) => {
    const question = questionText || input.trim();
    if (!question) return;

    // Mensagem do usuário
    const userMsg = { 
      id: `u-${Date.now()}`, 
      sender: "user", 
      text: question, 
      sending: !isRetry 
    };
    
    if (!isRetry) {
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setError(null);
      setRetryCount(0);
    }

    // Estado do bot
    setIsThinking(true);
    setBotEmoji(getBotEmoji(question));
    setSuggestions([]);

    try {
      // Pequena espera para mostrar "pensando"
      await new Promise((r) => setTimeout(r, Math.floor(Math.random() * 500) + 300));

      let data;
      let currentRetries = retryCount;

      // Tentar com retry
      while (currentRetries <= MAX_RETRIES) {
        try {
          data = await fetchWithTimeout(question, currentRetries);
          break; // Sucesso, sair do loop
        } catch (err) {
          if (err.message === "RETRY" && currentRetries < MAX_RETRIES) {
            currentRetries++;
            setRetryCount(currentRetries);
            // Aguardar antes de tentar novamente (backoff exponencial)
            // Capturar o valor atual para evitar referência insegura no loop
            const retryDelay = 1000 * currentRetries;
            await new Promise((r) => setTimeout(r, retryDelay));
            continue;
          }
          throw err;
        }
      }

      // Marca user message como enviada
      setMessages((prev) =>
        prev.map((m) => (m.id === userMsg.id ? { ...m, sending: false } : m))
      );

      const answer = data?.answer || "Desculpe, não consegui responder.";
      const newSuggestions = data?.suggestions || [];

      // Atualizar sugestões
      if (newSuggestions.length > 0) {
        setSuggestions(newSuggestions);
      }

      // Digitação letra por letra
      await typeMessage(answer);
      
      setRetryCount(0);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      
      setMessages((prev) =>
        prev.map((m) => (m.id === userMsg.id ? { ...m, sending: false } : m))
      );
      
      const errorMessage = err.message || "Houve um erro ao processar sua pergunta. Pode tentar novamente?";
      setError(errorMessage);
      
      // Mensagem de erro amigável
      await typeMessage(errorMessage);
      
      // Mostrar sugestões padrão em caso de erro
      setSuggestions([
        "Quais serviços vocês oferecem?",
        "Como agendar um orçamento?",
        "Quais são os métodos de pagamento?"
      ]);
    } finally {
      setIsThinking(false);
      setBotEmoji("🤖");
      abortControllerRef.current = null;
    }
  }, [input, retryCount, fetchWithTimeout, typeMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isThinking) {
        sendMessage();
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    sendMessage(suggestion, false);
  };

  const handleRetry = () => {
    const lastUserMessage = [...messages].reverse().find(m => m.sender === "user");
    if (lastUserMessage) {
      sendMessage(lastUserMessage.text, true);
    }
  };

  useEffect(() => {
    return () => {
      // Interrompe qualquer digitação ao desmontar
      typingAbortRef.current = true;
      // Cancela requisições pendentes
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <div className="fixed left-3 bottom-3 xs:left-4 xs:bottom-4 xs2:left-5 xs2:bottom-5 
                   sm:left-6 sm:bottom-6 md:left-8 md:bottom-8 z-50 fab-chatbot">
      {/* Botão flutuante (lado esquerdo, para não conflitar com o WhatsApp da direita) */}
      <button
        aria-label={isOpen ? "Fechar chat" : "Abrir chat"}
        onClick={() => setIsOpen((v) => !v)}
        className="bg-gradient-to-br from-primary-600 to-primary-700 text-white p-3 xs:p-3 sm:p-4 md:p-4 
                   rounded-full shadow-lg hover:from-primary-700 hover:to-primary-800 
                   transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400 
                   hover:scale-110 active:scale-95"
      >
        {isOpen ? (
          <FaTimes size={20} />
        ) : (
          <FaRobot size={24} className="animate-pulse" />
        )}  
      </button>

      {/* Caixa de chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="w-96 max-w-[92vw] h-[520px] bg-white shadow-2xl rounded-2xl overflow-hidden mt-3 border border-gray-200"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-3 flex items-center justify-between shadow-md">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <FaRobot className="text-white" size={20} />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-sm">Assistente Virtual</span>
                  <span className="text-xs text-primary-100">Notemaster</span>
                </div>
                <span className="ml-2 text-lg">{botEmoji}</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/90 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Fechar chat"
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* Área de mensagens */}
            <div className="flex-1 p-3 overflow-y-auto bg-gray-50">
              <div className="space-y-3">
                {messages.map((msg) => (
                            <MessageBubble key={msg.id} msg={msg} isThinking={isThinking} />
        ))}

                {/* Bolhas de pensamento enquanto pensa */}
                {isThinking && (
                  <div className="flex justify-start">
                    <div
                      className={`bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-2xl rounded-bl-sm shadow-sm ${
                        bubblePulse ? "animate-pulse" : ""
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span className="animate-bounce">●</span>
                        <span className="animate-bounce [animation-delay:120ms]">●</span>
                        <span className="animate-bounce [animation-delay:240ms]">●</span>
                        <span className="ml-2">{botEmoji}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div ref={messagesEndRef} />
            </div>

            {/* Sugestões */}
            {suggestions.length > 0 && !isThinking && (
              <div className="border-t border-gray-200 bg-gray-50 p-2">
                <div className="text-xs text-gray-600 mb-1 px-2">Sugestões:</div>
                <div className="flex flex-wrap gap-1">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="text-xs bg-white hover:bg-primary-50 text-gray-700 hover:text-primary-600 
                               px-2 py-1 rounded-lg border border-gray-200 hover:border-primary-300 
                               transition-colors duration-200"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-gray-200 bg-white p-2">
              {error && retryCount > 0 && (
                <div className="mb-2 flex items-center justify-between text-xs text-red-600 bg-red-50 
                              px-2 py-1 rounded">
                  <span>Erro na conexão. Tentando novamente...</span>
                  <button
                    onClick={handleRetry}
                    className="text-red-700 hover:text-red-900"
                    aria-label="Tentar novamente"
                  >
                    <FaRedo className="inline" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder="Digite sua pergunta..."
                  disabled={isThinking}
                  className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 
                           focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 
                           disabled:cursor-not-allowed"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={isThinking || !input.trim()}
                  className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed 
                           text-white px-4 rounded-xl transition-colors duration-200 
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isThinking ? "..." : "Enviar"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ChatbotWidget;

